import {
  useEffect,
  useReducer,
  useState
} from 'react';
import { Action } from 'redux';

import {
  AxiosAction,
  isSuccessActionOf,
  useAxiosDispatch
} from '../middleware/axios';
import { loaderReducer } from './delayedLoader';

const noop = ( ) => { /* do nothing */ };

export type ListState = 'error' | 'loading' | 'loaded';

/**
 * A custom hook which implements item loading logic for a list component,
 * and returns the list's current state (loading/loaded/errored).
 */
export function useListState({
  getItemsAction,
  getCountAction,
  clearRecentlyModified,
  page = 0,
  setPage = noop, // default to no-op so nothing happens
  maxPage = Infinity
}: {
  /**
   * A redux action creator which returns the action to be used to get all
   * items for this list component.
   * Ensure that this is wrapped in useCallback as appropriate, to avoid it
   * changing whenever your component re-renders with different props, which
   * would cause the items to be re-fetched on each render.
   */
  getItemsAction: () => AxiosAction<any>;
  /**
   * A redux action creator which returns the action to be used to get the
   * total count of items for this list component.
   * Can be omitted, if the list component doesn't need to support pagination
   * and therefore the count can be taken from the items array length.
   */
  getCountAction?: () => AxiosAction<any>;
  /**
   * A redux action creator which returns the action to be used to clear
   * the list of recently modified items for this list component.
   * Can be omitted, it is used so that the list of recently modified items
   * is cleared whenever the component is unmounted.
   */
  clearRecentlyModified?: () => Action;

  /** The current page, if your component supports pagination. */
  page?: number;
  /** A callback to set the current page, if you component supports pagination. */
  setPage?: (page: number) => void;
  /** The max page allowed, if your component supports pagination. */
  maxPage?: number;
}): ListState {
  const dispatch = useAxiosDispatch( );
  const [ failedToLoad, setFailedToLoad ] = useState(false);
  const [ loadingState, loadingDispatch ] = useReducer(loaderReducer, {
    displayLoader: true,
    loading: true
  });

  // if a clearRecentlyModified action is provided, then when unmounting
  // the component we want to trigger the action to avoid recently modified
  // items sticking around forever
  useEffect(function setupUnmountHandler( ) {
    return clearRecentlyModified && function onUnmount( ) {
      dispatch(clearRecentlyModified( ));
    };
  }, [ dispatch, clearRecentlyModified ]);

  // fetch the items and count whenever the callbacks to get their respective
  // actions change, meaning that their dependencies changed
  useEffect(function loadCount( ) {
    if (getCountAction) {
      dispatch(getCountAction( ));
    }
  }, [ dispatch, getCountAction ]);
  useEffect(function loadItems( ) {
    loadingDispatch('loading');
    loadingDispatch('display');

    // if page/maxPage/setPage aren't provided, then this will be false
    // by default.
    const isOffEndOfPageRange = maxPage !== 0 && page >= maxPage;
    if (isOffEndOfPageRange) {
      // once the page is decreased to a number which is within the page range,
      // this will go down the `else` path and the items will be loaded
      setPage(page - 1);
    } else {
      let isActiveLoad = true;

      const action = getItemsAction( );
      dispatch(action).then(function handleGetItemsResult(result) {
        if (!isActiveLoad) {
          // avoid race condition if by the time the getItems action
          // completes, a new one has been issued
          return;
        }
        const isSuccess = isSuccessActionOf(action.type)(result);
        setFailedToLoad(!isSuccess);
        loadingDispatch('loaded');
      });

      return ( ) => void (isActiveLoad = false);
    }
  }, [ dispatch, getItemsAction, maxPage, page, setPage ]);

  return failedToLoad ? 'error'
    : loadingState.displayLoader ? 'loading'
    : 'loaded';
}
